import type { Ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import { FilterParam } from "@/type";
import { Controllers, FrappeMethodType } from "@/type/api";
import {
  buildFrappeLikeSearchFilters,
  serializeFrappeFilters,
} from "@/services/frappe/filters";
import {
  fetchFrappeDocCount,
  normalizeFrappeListResult,
  withFrappeLoading,
  type FrappeListResult,
} from "@/services/frappe/list";
import {
  PatientEncounterResource,
  UpsertPatientEncounterPayload,
  Visit,
} from "@/type/visit";

const patientService = new HttpClient(Controllers.patient);

function buildVisitSearchParams(filter?: FilterParam): FilterParam {
  const { search, ...rest } = filter ?? {};

  const searchOrFilters = serializeFrappeFilters(
    buildFrappeLikeSearchFilters(
      ["custom_pet_id.pet_name", "custom_guardian_id.full_name", "encounter_comment"],
      search,
    ),
  );

  return {
    ...rest,
    or_filters: rest.or_filters ?? searchOrFilters,
  };
}

function normalizePatientEncounter(data: any): PatientEncounterResource | null {
  if (!data)
    return null;

  return {
    name: data.name,
    custom_guardian_id: data.custom_guardian_id,
    custom_pet_id: data.custom_pet_id,
    encounter_comment: data.encounter_comment ?? null,
    custom_total_amount: typeof data.custom_total_amount === "number" ? data.custom_total_amount : undefined,
    practitioner_name: typeof data.practitioner_name === "string" ? data.practitioner_name : undefined,
    practitioner_image: typeof data.practitioner_image === "string" ? data.practitioner_image : null,
    custom_services: Array.isArray(data.custom_services)
      ? data.custom_services.map((service: any) => ({
          care_service_id: service?.care_service_id ?? null,
          pet_care_service_id: service?.pet_care_service_id ?? null,
        }))
      : [],
    status: data.status,
    encounter_date: data.encounter_date,
    encounter_time: data.encounter_time,
  };
}

export async function CreateVisit(
  loading: Ref<boolean>,
  payload: UpsertPatientEncounterPayload,
): Promise<string | null> {
  return withFrappeLoading(loading, async () => {
    try {
      const response = await patientService.post<{ data: { name: string } }, UpsertPatientEncounterPayload>(
        payload,
        "",
      );

      return response?.data?.name ?? null;
    } catch (error: any) {
      // Re-throw instead of swallowing to null — the backend's reason (e.g. deceased pet) is on
      // error.message. The one consumer (SaveHealthcareVisit's legacy fallback) decides how to
      // handle it; low-level creators should not silently hide failures.
      console.error(error?.message ?? error);
      throw error;
    }
  });
}

export async function GetVisitById(
  loading: Ref<boolean>,
  id: string,
): Promise<PatientEncounterResource | null> {
  return withFrappeLoading(loading, async () => {
    try {
      const response = await patientService.get<any>(
        "pet_app.api.visit.get_visit",
        { encounter_id: id },
        undefined,
        undefined,
        undefined,
        FrappeMethodType.method,
      );

      return normalizePatientEncounter(response?.data ?? response?.message ?? null);
    } catch (error: any) {
      console.error(error?.message ?? error);
      return null;
    }
  });
}

export async function UpdateVisit(
  loading: Ref<boolean>,
  id: string,
  payload: UpsertPatientEncounterPayload,
): Promise<boolean> {
  return withFrappeLoading(loading, async () => {
    try {
      await patientService.put<{ data: PatientEncounterResource }, UpsertPatientEncounterPayload>(
        undefined,
        encodeURIComponent(id),
        payload,
      );

      return true;
    } catch (error: any) {
      console.error(error?.message ?? error);
      return false;
    }
  });
}

export async function GetVisits(
  loading: Ref<boolean>,
  filter: FilterParam,
): Promise<FrappeListResult<Visit> | null> {
  return withFrappeLoading(loading, async () => {
    try {
      const params = buildVisitSearchParams(filter);
      const response = await patientService.get<{ data: Visit[] }>(
        "",
        params,
        undefined,
        JSON.stringify([
          "name",
          "encounter_date",
          "encounter_time",
          "practitioner",
          "practitioner.practitioner_name",
          "practitioner.image",
          "encounter_comment",
          "custom_pet_id",
          "custom_pet_id.pet_name",
          "custom_pet_id.pet_image",
          "custom_guardian_id",
          "custom_guardian_id.full_name",
          "custom_guardian_id.guardian_image",
        ]),
      );

      const total = await fetchFrappeDocCount(
        patientService,
        "Patient Encounter",
        params.filters,
        params.or_filters,
      ).catch(() => response?.data?.length ?? 0);

      return normalizeFrappeListResult(response, total);
    } catch (error: any) {
      console.error(error?.message ?? error);
      return null;
    }
  });
}
